import React, { useState, useEffect, useCallback } from "react";
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from "react-beautiful-dnd";
import Cropper, { Area } from "react-easy-crop";
import Modal from "react-modal";
import getCroppedImg from "../ImageCrop/cropImage";
import BackButton from "../shared/BackButton/BackButton";
import styles from "./Landmarks.module.css";
import { fetchLandmarks, updateLandmarks } from "../API/Landmarks/LandmarksAPI";

import { LandmarkData, ImagesData } from "../API/types";
import { uploadImage } from "../API/API";

// --- Constants for Language Selection ---
const AVAILABLE_LANGUAGES = [
  "English",
  "Spanish",
  "Italian",
  "French",
  "German",
  "Portuguese",
];

const langCodeMap: { [key: string]: string } = {
  English: "EN",
  Spanish: "ES",
  Italian: "IT",
  French: "FR",
  German: "DE",
  Portuguese: "PT",
};

// --- Helper Function ---
const reorder = (
  list: LandmarkData[],
  startIndex: number,
  endIndex: number
): LandmarkData[] => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);
  return result;
};

Modal.setAppElement("#root");

const CitiesManager: React.FC = () => {
  // --- State ---
  const [sourceLanguage, setSourceLanguage] = useState<string>("");
  const [landmarks, setLandmarks] = useState<LandmarkData[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  // Edit form state
  const [showEditForm, setShowEditForm] = useState<boolean>(false);
  const [currentEditingLandmark, setCurrentEditingLandmark] =
    useState<LandmarkData | null>(null);
  const [editCity, setEditCity] = useState<string>("");
  const [editCountry, setEditCountry] = useState<string>("");
  const [editDescription, setEditDescription] = useState<string>("");
  const [editImages, setEditImages] = useState<ImagesData[]>([]);
  const [editSite, setEditSite] = useState<string>("");
  const [editSubtitle, setEditSubtitle] = useState<string>("");
  const [editLat, setEditLat] = useState<string>("");
  const [editLon, setEditLon] = useState<string>("");

  // Reorder state
  const [isReorderMode, setIsReorderMode] = useState<boolean>(false);
  const [reorderedLandmarks, setReorderedLandmarks] = useState<LandmarkData[]>(
    []
  );

  // --- Cropping State ---
  const [croppingFile, setCroppingFile] = useState<File | null>(null);
  const [croppingDialogOpen, setCroppingDialogOpen] = useState<boolean>(false);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [isCropping, setIsCropping] = useState<boolean>(false);

  // --- Load Landmarks ---
  const loadLandmarks = useCallback(async () => {
    if (!sourceLanguage) {
      setLandmarks([]);
      setReorderedLandmarks([]);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const targetLangCode = langCodeMap[sourceLanguage];
      const data = await fetchLandmarks("general", targetLangCode);
      setLandmarks(data);
      setReorderedLandmarks(data);
    } catch (err: any) {
      console.error("Error fetching landmarks:", err);
      setError(`Failed to fetch city data: ${err.message || "Unknown error"}`);
      setLandmarks([]);
      setReorderedLandmarks([]);
    } finally {
      setLoading(false);
    }
  }, [sourceLanguage]);

  useEffect(() => {
    loadLandmarks();
  }, [loadLandmarks]);

  // --- Edit Handlers ---
  const handleEditClick = (landmark: LandmarkData) => {
    setCurrentEditingLandmark(landmark);
    setEditCity(landmark.City);
    setEditCountry(landmark.Country);
    setEditDescription(landmark.Description);
    setEditImages([...landmark.Images]);
    setEditSite(landmark.Site || "");
    setEditSubtitle(landmark.Subtitle || "");
    setEditLat(landmark.Geo_Coordinates?.lat?.toString() ?? "");
    setEditLon(landmark.Geo_Coordinates?.lon?.toString() ?? "");
    setShowEditForm(true);
    setError(null);
  };

  const handleCropComplete = useCallback((_: Area, croppedArea: Area) => {
    setCroppedAreaPixels(croppedArea);
  }, []);

  const handleCrop = async () => {
    if (croppingFile && croppedAreaPixels) {
      setIsCropping(true);
      try {
        const croppedBlob = await getCroppedImg(
          URL.createObjectURL(croppingFile),
          croppedAreaPixels
        );
        const fileName = `${
          editCity || "city"
        }_image_${Date.now()}${croppingFile.name.substring(
          croppingFile.name.lastIndexOf(".")
        )}`;
        const uploadedUrl = await uploadImage(fileName, croppedBlob);
        setEditImages((prev) => [
          ...prev,
          { URL: uploadedUrl, Description: "" },
        ]);
      } catch (err) {
        console.error("Crop/upload error:", err);
        setError("Failed to crop or upload image.");
      } finally {
        setCroppingDialogOpen(false);
        setCroppingFile(null);
        setCrop({ x: 0, y: 0 });
        setZoom(1);
        setCroppedAreaPixels(null);
        setIsCropping(false);
      }
    }
  };

  const handleEditImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    if (file) {
      setCroppingFile(file);
      setCroppingDialogOpen(true);
    }
    e.target.value = "";
  };

  const handleEditSave = async () => {
    if (!currentEditingLandmark) return;
    setIsSaving(true);
    setError(null);

    const parsedLat = parseFloat(editLat);
    const parsedLon = parseFloat(editLon);
    const safeLat = !isNaN(parsedLat)
      ? parsedLat
      : currentEditingLandmark.Geo_Coordinates?.lat ?? 0;
    const safeLon = !isNaN(parsedLon)
      ? parsedLon
      : currentEditingLandmark.Geo_Coordinates?.lon ?? 0;

    const updatedLandmark: LandmarkData = {
      ...currentEditingLandmark,
      City: editCity,
      Country: editCountry,
      Description: editDescription,
      Images: editImages,
      Site: editSite,
      Subtitle: editSubtitle,
      Geo_Coordinates: { lat: safeLat, lon: safeLon },
    };

    const updatedList = landmarks.map((lm) =>
      lm.Identifier === currentEditingLandmark.Identifier ? updatedLandmark : lm
    );

    try {
      const targetLangCode = langCodeMap[sourceLanguage];
      await updateLandmarks(targetLangCode, "general", updatedList);
      setLandmarks(updatedList);
      setReorderedLandmarks(updatedList);
      setShowEditForm(false);
      setCurrentEditingLandmark(null);
      alert("City updated successfully!");
    } catch (err: any) {
      console.error("Error saving landmark:", err);
      setError(`Failed to save changes: ${err.message || "Unknown API error"}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleEditCancel = () => {
    setShowEditForm(false);
    setCurrentEditingLandmark(null);
    setError(null);
  };

  // --- Reorder Handlers ---
  const onDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    const newOrder = reorder(
      reorderedLandmarks,
      result.source.index,
      result.destination.index
    );
    setReorderedLandmarks(newOrder);
  };

  const handleSaveReorder = () => {
    setLandmarks(reorderedLandmarks);
    setIsReorderMode(false);
    alert(
      "City order updated locally! Save edits on an item to persist the new order."
    );
  };

  const handleCancelReorder = () => {
    setReorderedLandmarks(landmarks);
    setIsReorderMode(false);
  };

  // --- Rendering ---
  return (
    <div className={styles.container}>
      <BackButton />
      <h1 className={styles.title}>City Manager</h1>

      {/* Language Dropdown */}
      <div className={styles.formRow}>
        <label className={styles.label}>
          <strong>Choose Language:</strong>
          <select
            value={sourceLanguage}
            onChange={(e) => setSourceLanguage(e.target.value)}
            className={styles.input}
            disabled={loading || isSaving || isReorderMode || showEditForm}
          >
            <option value="">Select a language</option>
            {AVAILABLE_LANGUAGES.map((lang) => (
              <option key={lang} value={lang}>
                {lang}
              </option>
            ))}
          </select>
        </label>
      </div>

      {/* Status Messages */}
      {loading && <p>Loading cities...</p>}
      {isSaving && <p>Saving changes...</p>}
      {error && !showEditForm && <div className={styles.error}>{error}</div>}

      {/* Table + Reorder */}
      {!loading && !error && sourceLanguage && landmarks.length > 0 && (
        <>
          <button
            onClick={() => {
              setReorderedLandmarks([...landmarks]);
              setIsReorderMode(true);
            }}
            className={styles.actionButton}
            disabled={isSaving || showEditForm}
          >
            Reorder Cities
          </button>

          <table className={styles.table}>
            <thead>
              <tr>
                <th>Identifier</th>
                <th>City</th>
                <th>Country</th>
                <th>Site</th>
                <th>Subtitle</th>
                <th>Description</th>
                <th>Coordinates</th>
                <th>Images</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {landmarks.map((landmark) => (
                <tr key={landmark.Identifier}>
                  <td>{landmark.Identifier}</td>
                  <td>{landmark.City}</td>
                  <td>{landmark.Country}</td>
                  <td>{landmark.Site || "N/A"}</td>
                  <td>{landmark.Subtitle || "N/A"}</td>
                  <td title={landmark.Description}>
                    {landmark.Description.length > 50
                      ? `${landmark.Description.substring(0, 50)}...`
                      : landmark.Description}
                  </td>
                  <td>
                    {landmark.Geo_Coordinates?.lat?.toFixed(4) ?? "N/A"},{" "}
                    {landmark.Geo_Coordinates?.lon?.toFixed(4) ?? "N/A"}
                  </td>
                  <td>
                    {landmark.Images.slice(0, 2).map((img, idx) => (
                      <img
                        key={idx}
                        src={img.URL}
                        alt={img.Description || `Image ${idx + 1}`}
                        title={`${img.URL}${
                          img.Description ? ` - ${img.Description}` : ""
                        }`}
                        style={{
                          width: "40px",
                          height: "40px",
                          objectFit: "cover",
                          marginRight: "5px",
                          border: "1px solid #ccc",
                        }}
                        onError={(e) =>
                          (e.currentTarget.style.display = "none")
                        }
                      />
                    ))}
                    {landmark.Images.length > 2 && <span>...</span>}
                    {landmark.Images.length === 0 && <span>No images</span>}
                  </td>
                  <td>
                    <button
                      onClick={() => handleEditClick(landmark)}
                      disabled={isSaving || isReorderMode}
                      className={styles.button}
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {!loading && !error && sourceLanguage && landmarks.length === 0 && (
        <p>No landmarks found for the selected language.</p>
      )}

      {/* --- Edit City Modal --- */}
      {showEditForm && currentEditingLandmark && (
        <div className={styles.modalBackdrop} onClick={handleEditCancel}>
          <div
            className={styles.modalContent}
            onClick={(e) => e.stopPropagation()}
          >
            <h2>Edit City: {currentEditingLandmark.Identifier}</h2>
            {error && (
              <div className={`${styles.error} ${styles.modalError}`}>
                {error}
              </div>
            )}

            <label>
              City:
              <input
                type="text"
                value={editCity}
                onChange={(e) => setEditCity(e.target.value)}
                disabled={isSaving}
                className={styles.input}
              />
            </label>
            <label>
              Country:
              <input
                type="text"
                value={editCountry}
                onChange={(e) => setEditCountry(e.target.value)}
                disabled={isSaving}
                className={styles.input}
              />
            </label>
            <label>
              Site:
              <input
                type="text"
                value={editSite}
                onChange={(e) => setEditSite(e.target.value)}
                disabled={isSaving}
                className={styles.input}
              />
            </label>
            <label>
              Subtitle:
              <input
                type="text"
                value={editSubtitle}
                onChange={(e) => setEditSubtitle(e.target.value)}
                disabled={isSaving}
                className={styles.input}
              />
            </label>
            <label>
              Paste Coordinates (lat, lon):
              <input
                type="text"
                placeholder="e.g., 53.50572623610362, 9.96787781918061"
                onPaste={(e) => {
                  const pastedText = e.clipboardData.getData('text');
                  const coords = pastedText.split(',').map(s => s.trim());
                  if (coords.length === 2) {
                    const lat = parseFloat(coords[0]);
                    const lon = parseFloat(coords[1]);
                    if (!isNaN(lat) && !isNaN(lon)) {
                      setEditLat(lat.toString());
                      setEditLon(lon.toString());
                      e.preventDefault();
                    }
                  }
                }}
                disabled={isSaving}
                className={styles.input}
              />
            </label>
            <label>
              Latitude:
              <input
                type="number"
                step="any"
                value={editLat}
                onChange={(e) => setEditLat(e.target.value)}
                disabled={isSaving}
                className={styles.input}
              />
            </label>
            <label>
              Longitude:
              <input
                type="number"
                step="any"
                value={editLon}
                onChange={(e) => setEditLon(e.target.value)}
                disabled={isSaving}
                className={styles.input}
              />
            </label>
            <label>
              Description:
              <textarea
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
                disabled={isSaving}
                className={styles.textarea}
                rows={4}
              />
            </label>

            {/* Images with Crop */}
            <fieldset className={styles.imageSection}>
              <legend>Images</legend>
              <label>
                Upload & Crop Image:
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleEditImageUpload}
                  disabled={isSaving}
                  className={styles.input}
                />
              </label>
              <div className={styles.imageList}>
                {editImages.map((img, idx) => (
                  <div key={idx} className={styles.imageEditItem}>
                    <img
                      src={img.URL}
                      alt={img.Description || `Image ${idx}`}
                      style={{
                        width: 50,
                        height: 50,
                        objectFit: "cover",
                        marginRight: 10,
                      }}
                      onError={(e) => (e.currentTarget.src = "placeholder.png")}
                    />
                    <input
                      type="text"
                      placeholder="Description"
                      value={img.Description}
                      onChange={(e) => {
                        const desc = e.target.value;
                        setEditImages((prev) =>
                          prev.map((im, i) =>
                            i === idx ? { ...im, Description: desc } : im
                          )
                        );
                      }}
                      className={styles.input}
                      disabled={isSaving}
                    />
                    <button
                      onClick={() =>
                        setEditImages((prev) =>
                          prev.filter((_, i) => i !== idx)
                        )
                      }
                      disabled={isSaving}
                      className={styles.removeButton}
                    >
                      Remove
                    </button>
                  </div>
                ))}
                {editImages.length === 0 && <p>No images.</p>}
              </div>

              {/* Cropping Modal */}
              <Modal
                isOpen={croppingDialogOpen}
                onRequestClose={() => setCroppingDialogOpen(false)}
                overlayClassName={styles.cropOverlay}
                className={styles.cropContent}
              >
                {croppingFile && (
                  <div className={styles.cropContainer}>
                    <Cropper
                      image={URL.createObjectURL(croppingFile)}
                      crop={crop}
                      zoom={zoom}
                      aspect={4 / 3}
                      onCropChange={setCrop}
                      onZoomChange={setZoom}
                      onCropComplete={handleCropComplete}
                    />
                    <button onClick={handleCrop} className={styles.cropButton} disabled={isCropping}>
                    {isCropping
                        ? <><span className={styles.spinner}/>Cropping…</>
                        : "Crop & Upload"}
                    </button>
                  </div>
                )}
              </Modal>
            </fieldset>

            <div className={styles.modalActions}>
              <button
                onClick={handleEditSave}
                disabled={isSaving}
                className={styles.saveButton}
              >
                {isSaving ? "Saving..." : "Save Changes"}
              </button>
              <button
                onClick={handleEditCancel}
                disabled={isSaving}
                className={styles.clearButton}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reorder Modal */}
      {isReorderMode && (
        <div className={styles.modalBackdrop} onClick={handleCancelReorder}>
          {/* Add a specific class for easier styling if needed, e.g., reorderModalContent */}
          <div
            className={`${styles.modalContent} ${styles.reorderModalContent}`}
            onClick={(e) => e.stopPropagation()}
          >
            <h2>Reorder Cities</h2>
            <p>Drag and drop rows to change the order.</p>
            {/* This div will contain the DragDropContext and the scrollable table */}
            <div className={styles.reorderTableWrapper}>
              <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId="citiesDroppable">
                  {(provided) => (
                    // This div becomes the scrollable container and the Droppable area
                    <div
                      ref={provided.innerRef} // Apply ref here
                      {...provided.droppableProps} // Apply droppableProps here
                      className={styles.scrollableTableContainer} // Apply scroll styling class here
                    >
                      <table
                        className={`${styles.table} ${styles.reorderTable}`}
                        // Removed droppableProps and ref from table
                      >
                        <thead>
                          <tr>
                            <th></th> {/* Drag Handle */}
                            <th>Identifier</th>
                            <th>City</th>
                            <th>Country</th>
                          </tr>
                        </thead>
                        <tbody>
                          {reorderedLandmarks.map((landmark, index) => (
                            <Draggable
                              key={landmark.Identifier}
                              draggableId={landmark.Identifier}
                              index={index}
                            >
                              {(providedDraggable, snapshot) => (
                                <tr
                                  ref={providedDraggable.innerRef}
                                  {...providedDraggable.draggableProps}
                                  // Drag handle props moved to the specific TD
                                  className={
                                    snapshot.isDragging
                                      ? styles.draggingItem
                                      : ""
                                  }
                                >
                                  <td
                                    {...providedDraggable.dragHandleProps}
                                    className={styles.dragHandle}
                                  >
                                    ☰
                                  </td>
                                  <td>{landmark.Identifier}</td>
                                  <td>{landmark.City}</td>
                                  <td>{landmark.Country}</td>
                                </tr>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}{" "}
                          {/* Placeholder goes inside the scrollable div */}
                        </tbody>
                      </table>
                    </div> // End scrollableTableContainer
                  )}
                </Droppable>
              </DragDropContext>
            </div>{" "}
            {/* End reorderTableWrapper */}
            <div className={styles.modalActions}>
              <button onClick={handleSaveReorder} className={styles.saveButton}>
                Save Order Locally
              </button>
              <button
                onClick={handleCancelReorder}
                className={styles.clearButton}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CitiesManager;
